import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useI18n } from "../i18n/I18nContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { Info } from "lucide-react";

function Field({ id, label, error, children, required }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-base">
        {label} {required && <span className="text-[#2A9D8F]">*</span>}
      </Label>
      {children}
      {error && <p className="text-sm text-red-600" data-testid={`${id}-error`}>{error.message}</p>}
    </div>
  );
}

export default function LeadForm({ kind }) {
  const { t, lang } = useI18n();
  const { register, handleSubmit, reset, setValue, watch, formState: { errors, isSubmitting } } = useForm({
    defaultValues: { consent: false },
  });
  const [done, setDone] = useState(false);
  const consentVal = watch("consent");
  register("consent", { validate: (v) => v === true || t("forms.error") });

  const endpoint = { visit: "/forms/visit", contact: "/forms/contact", careers: "/forms/careers" }[kind];
  const testid = `${kind}-form`;

  const onSubmit = async (values) => {
    try {
      await api.post(endpoint, { ...values, lang });
      toast.success(t("forms.success"));
      setDone(true);
      reset();
    } catch (e) {
      const msg = e?.response?.data?.detail || t("forms.error");
      toast.error(typeof msg === "string" ? msg : t("forms.error"));
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" data-testid={testid} noValidate>
      {/* honeypot */}
      <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true"
        className="hidden" {...register("hp")} />

      <div className="grid gap-5 sm:grid-cols-2">
        <Field id="name" label={t("forms.name")} error={errors.name} required>
          <Input id="name" data-testid={`${testid}-name`} className="min-h-[44px]"
            {...register("name", { required: `${t("forms.name")} ${t("common.required")}` })} />
        </Field>
        <Field id="email" label={t("forms.email")} error={errors.email} required>
          <Input id="email" type="email" data-testid={`${testid}-email`} className="min-h-[44px]"
            {...register("email", { required: `${t("forms.email")} ${t("common.required")}` })} />
        </Field>
        <Field id="phone" label={t("forms.phone")} error={errors.phone} required={kind !== "contact"}>
          <Input id="phone" data-testid={`${testid}-phone`} className="min-h-[44px]"
            {...register("phone", kind !== "contact" ? { required: `${t("forms.phone")} ${t("common.required")}` } : {})} />
        </Field>
        {kind === "visit" && (
          <Field id="preferred_date" label={t("forms.date")}>
            <Input id="preferred_date" type="date" data-testid={`${testid}-date`} className="min-h-[44px]"
              {...register("preferred_date")} />
          </Field>
        )}
        {kind === "contact" && (
          <Field id="subject" label={t("forms.subject")}>
            <Input id="subject" data-testid={`${testid}-subject`} className="min-h-[44px]" {...register("subject")} />
          </Field>
        )}
        {kind === "careers" && (
          <>
            <Field id="position" label={t("forms.position")}>
              <Input id="position" data-testid={`${testid}-position`} className="min-h-[44px]" {...register("position")} />
            </Field>
            <Field id="portfolio_url" label={t("forms.portfolio")}>
              <Input id="portfolio_url" data-testid={`${testid}-portfolio`} className="min-h-[44px]" {...register("portfolio_url")} />
            </Field>
          </>
        )}
      </div>

      <Field id="message" label={t("forms.message")} error={errors.message} required={kind === "contact"}>
        <Textarea id="message" rows={4} data-testid={`${testid}-message`}
          {...register("message", kind === "contact" ? { required: `${t("forms.message")} ${t("common.required")}` } : {})} />
      </Field>

      <p className="flex items-start gap-2 rounded-lg bg-[#EEF1E9] p-3 text-sm text-[#5C6370]">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-[#2A9D8F]" /> {t("forms.noMedical")}
      </p>

      <label className="flex items-start gap-3 text-sm">
        <Checkbox data-testid={`${testid}-consent`} className="mt-1" checked={!!consentVal}
          onCheckedChange={(v) => setValue("consent", !!v, { shouldValidate: true })} />
        <span>{kind === "careers" ? t("forms.consentCv") : t("forms.consent")} <span className="text-[#2A9D8F]">*</span></span>
      </label>
      {errors.consent && <p className="text-sm text-red-600" data-testid={`${testid}-consent-error`}>{t("forms.consent")}</p>}

      <Button type="submit" disabled={isSubmitting} data-testid={`${testid}-submit`}
        className="rounded-full min-h-[48px] bg-[#2A9D8F] px-8 hover:bg-[#21867A]">
        {isSubmitting ? t("common.sending") : t("common.send")}
      </Button>
      {done && <p className="text-sm font-medium text-[#2A9D8F]" data-testid={`${testid}-done`}>{t("forms.success")}</p>}
    </form>
  );
}
